import React, { useState } from "react";
import { useGame } from "../game/em2c-store";
import { Info, Lightbulb, Zap, Home, FlaskConical, CloudFog } from "lucide-react";

const HINT_SEQUENCE = [
  {
    id: "welcome",
    title: "Welcome to Mars",
    text: "Welcome to your new Mars colony! Build structures, manage resources, and keep your colonists alive.",
    Icon: Info,
    condition: () => true,
  },
  {
    id: "solar",
    title: "Build your first solar panel",
    text: "Your colony needs power to keep systems online. Construct a solar panel or power building to generate electricity.",
    Icon: Zap,
    condition: ({ buildings }) => {
      const bList = Array.isArray(buildings) ? buildings : [];
      const hasPower = bList.some((b) => {
        const type = (b?.type || "").toLowerCase();
        const name = (b?.name || "").toLowerCase();
        return type.includes("solar") || type.includes("power") || name.includes("solar") || name.includes("power");
      });
      return !hasPower;
    },
  },
  {
    id: "habitat",
    title: "Build a habitat",
    text: "Colonists require living quarters to survive on Mars. Construct a habitat building before welcoming crew.",
    Icon: Home,
    condition: ({ buildings, colonists }) => {
      const colonistsCount = Array.isArray(colonists) ? colonists.length : (typeof colonists === "number" ? colonists : 0);
      const bList = Array.isArray(buildings) ? buildings : [];
      const hasHabitat = bList.some((b) => {
        const type = (b?.type || "").toLowerCase();
        const name = (b?.name || "").toLowerCase();
        return type.includes("habitat") || type.includes("habitation") || name.includes("habitat") || name.includes("habitation");
      });
      return colonistsCount === 0 && !hasHabitat;
    },
  },
  {
    id: "research",
    title: "Research new tech",
    text: "You have accumulated enough credits! Open the Research menu to unlock advanced structures and technologies.",
    Icon: FlaskConical,
    condition: ({ credits, research }) => {
      const completedCount = Array.isArray(research?.completed) ? research.completed.length : 0;
      return credits >= 50 && completedCount === 0;
    },
  },
  {
    id: "storm",
    title: "Watch for storms",
    text: "A Martian dust storm warning is active! Dust storms lower solar generation and put colony structures at risk.",
    Icon: CloudFog,
    condition: ({ storm }) => {
      return Boolean(storm?.warning);
    },
  },
];

export default function TutorialHints() {
  const [dismissed, setDismissed] = useState(new Set());

  const buildings = useGame((s) => s.buildings);
  const colonists = useGame((s) => s.colonists);
  const resources = useGame((s) => s.resources);
  const research = useGame((s) => s.research);
  const storm = useGame((s) => s.storm);

  const credits = typeof resources?.credits === "object"
    ? (resources.credits.stored ?? 0)
    : (typeof resources?.credits === "number" ? resources.credits : 0);

  const gameState = { buildings, colonists, resources, research, storm, credits };

  // Find first non-dismissed hint whose milestone condition is met
  const activeHint = HINT_SEQUENCE.find((hint) => {
    if (dismissed.has(hint.id)) return false;
    return hint.condition(gameState);
  });

  if (!activeHint) return null;

  const handleDismiss = () => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(activeHint.id);
      return next;
    });
  };

  const IconComponent = activeHint.Icon || Lightbulb;

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 max-w-md w-[90%] md:w-[420px] pointer-events-auto"
      style={{ fontFamily: "'Rajdhani', sans-serif" }}
    >
      <div className="bg-[#0a0d14]/95 border border-[#00fa9a]/40 shadow-[0_0_20px_rgba(0,250,154,0.15)] rounded-lg p-4 flex flex-col gap-2.5 text-slate-100 backdrop-blur-md transition-all duration-300">
        <div className="flex items-center justify-between gap-3 border-b border-[#00fa9a]/20 pb-2">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-[#00fa9a]/10 border border-[#00fa9a]/30 rounded text-[#00fa9a] shrink-0">
              <IconComponent size={18} />
            </div>
            <h4 className="font-bold text-base uppercase tracking-wider text-[#00fa9a]">
              {activeHint.title}
            </h4>
          </div>
        </div>

        <p className="text-sm text-slate-300 leading-relaxed font-medium">
          {activeHint.text}
        </p>

        <div className="flex justify-end pt-1">
          <button
            onClick={handleDismiss}
            className="px-4 py-1.5 bg-[#00fa9a]/15 hover:bg-[#00fa9a]/30 text-[#00fa9a] border border-[#00fa9a]/60 hover:border-[#00fa9a] rounded text-xs font-bold uppercase tracking-widest transition-all duration-200 cursor-pointer shadow-[0_0_8px_rgba(0,250,154,0.2)]"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export { TutorialHints };
